import { ListRenderItem } from '@shopify/flash-list';
import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { ICONS } from 'components/Icon';
import { List } from 'components/List';
import { Menu } from 'components/Menu';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { Page } from 'components/Page';
import { PageEmpty } from 'components/PageEmpty';
import { PageHeader } from 'components/PageHeader';
import { PageToolbar } from 'components/PageToolbar';
import { Txt } from 'components/Txt';
import { useConfirm } from 'hooks/useConfirm';
import { useEsc } from 'hooks/useEsc';
import { useSelector } from 'hooks/useSelector';
import { pluralise } from 'lib/pluralise';
import { getSearchTermsFromSearchValue } from 'lib/riskbase';
import { theme } from 'lib/theme';
import { without } from 'lodash';
import React from 'react';
import { useDispatch } from 'react-redux';
import {
  doAseSetupLibraryQuestionLinksChosen,
  NAVIGATE_MODAL_BACK,
  SET_GENERIC,
} from 'store/actions';
import { selectGeneric } from 'store/app/selectors';
import {
  selectAseSetupLibraryFolders,
  selectAseSetupLibraryItemList,
  selectAseSetupLibraryQuestions,
  selectAseSetupLibrarySections,
} from 'store/remote/selectors';

type ListItem = {
  id: number;
  question: string;
  folder: string;
  section: string;
  linkCount: number;
};

const Row: React.FC<{
  id: number;
  folder: string;
  section: string;
  question: string;
  linkCount: number;
  onPress: (id: number) => void;
  isChecked: boolean;
}> = React.memo(
  ({ id, folder, section, question, linkCount, onPress, isChecked }) => (
    <Card style={{ marginBottom: theme.paddingHorizontalHalf }}>
      <CardRow
        labelAlignTop
        label={question}
        onPress={() => onPress(id)}
        checkedValue={isChecked}
      >
        <Txt
          size="xxxsmall"
          style={{ color: theme.colors.fieldValueText, marginTop: 6 }}
        >
          <Txt size="xxxsmall" weight="bold">
            {folder}
          </Txt>{' '}
          •{' '}
          <Txt size="xxxsmall" weight="bold">
            {section}
          </Txt>{' '}
          •{' '}
          {pluralise(
            linkCount,
            '1 Question Link',
            '{x} Question Links',
            'No Question Links',
          )}
        </Txt>
      </CardRow>
    </Card>
  ),
);

export const LinkQuestionListScreen: React.FC<
  ModalScreenProps<ModalScreens.ASESETUPLIBRARYLINKQUESTIONS>
> = ({ route, navigation }) => {
  const { hideIds } = route.params;
  const dispatch = useDispatch();
  const confirm = useConfirm();
  const genericKeySearchString = 'GENERICKEY_QUESTIONLINKLISTFILTER';

  const textFieldKeyRef = React.useRef(Math.random());
  const doAutoFocusRef = React.useRef<undefined | boolean>(undefined);
  doAutoFocusRef.current = doAutoFocusRef.current === undefined;

  const searchValue =
    (useSelector((state) =>
      selectGeneric(state, genericKeySearchString),
    ) as string) || '';
  const searchValueRef = React.useRef(searchValue);

  const folders = useSelector(selectAseSetupLibraryFolders);
  const sections = useSelector(selectAseSetupLibrarySections);
  const questions = useSelector(selectAseSetupLibraryQuestions);
  const list = useSelector(selectAseSetupLibraryItemList);

  const questionItems = React.useMemo(
    () =>
      list.reduce<{ li: ListItem[]; c: string; s: string }>(
        (acc, listItem) => {
          if (listItem.type === 'question') {
            const question = questions[listItem.id];
            if (!hideIds.includes(listItem.id)) {
              acc.li.push({
                id: listItem.id,
                question: question?.question || 'Unknown',
                folder: acc.c,
                section: acc.s,
                linkCount: question?.linkedQuestionIds.length || 0,
              });
            }
          } else if (listItem.type === 'section') {
            acc.s = sections[listItem.id]?.name || 'Unknown';
          } else {
            acc.c = folders[listItem.id]?.name || 'Unknown';
          }
          return acc;
        },
        { li: [], c: 'Unknown', s: 'Unknown' },
      ).li,
    [list, folders, questions, sections, hideIds],
  );

  const filteredQuestionItems = React.useMemo((): ListItem[] => {
    if (!searchValue) {
      return questionItems;
    }
    const { flagHasLink, searchTerms } = getSearchTermsFromSearchValue(
      searchValue,
    ).reduce<{ flagHasLink: boolean | undefined; searchTerms: string[] }>(
      (acc, term) => {
        if (term) {
          if (term === '+link') {
            acc.flagHasLink = true;
          } else if (term === '-link') {
            acc.flagHasLink = false;
          } else {
            acc.searchTerms.push(term);
          }
        }
        return acc;
      },
      {
        flagHasLink: undefined,
        searchTerms: [],
      },
    );
    return questionItems.reduce<ListItem[]>((acc, questionItem) => {
      const searchString = [];
      if (flagHasLink !== undefined) {
        if (flagHasLink ? !questionItem.linkCount : questionItem.linkCount) {
          return acc;
        }
      }
      if (searchTerms.length) {
        if (questionItem.question) {
          searchString.push(questionItem.question);
        }
        if (questionItem.folder) {
          searchString.push(questionItem.folder);
        }
        if (questionItem.section) {
          searchString.push(questionItem.section);
        }
        const finalSearchString = searchString.join(' ').toLowerCase();
        if (
          !finalSearchString ||
          !searchTerms.every((term) => finalSearchString.includes(term))
        ) {
          return acc;
        }
      }
      acc.push(questionItem);
      return acc;
    }, []);
  }, [questionItems, searchValue]);

  const [checkedIds, setCheckedIds] = React.useState<number[]>([]);

  const handlePressSave = () => {
    dispatch(doAseSetupLibraryQuestionLinksChosen(checkedIds));
    dispatch(NAVIGATE_MODAL_BACK());
  };

  const handlePressRow = React.useCallback(
    (id: number) =>
      setCheckedIds((existing) =>
        existing.includes(id) ? without(existing, id) : [...existing, id],
      ),
    [],
  );

  const renderItem: ListRenderItem<ListItem> = React.useCallback(
    ({ item, extraData }) => {
      return (
        <Row
          id={item.id}
          folder={item.folder}
          section={item.section}
          question={item.question}
          linkCount={item.linkCount}
          onPress={handlePressRow}
          isChecked={extraData.checkedIds.includes(item.id)}
        />
      );
    },
    [handlePressRow],
  );

  const handleSearch = React.useCallback(
    (search: string) => {
      searchValueRef.current = search.trim();
      dispatch(
        SET_GENERIC({
          key: genericKeySearchString,
          value: search || undefined,
          temp: true,
        }),
      );
    },
    [dispatch],
  );

  const searchIncludes = React.useCallback(
    (searchText: string) =>
      searchValueRef.current.split(' ').includes(searchText),
    [],
  );

  const handleAddFilter = React.useCallback(
    (addFilter: string, removeFilter: string) => {
      textFieldKeyRef.current = Math.random();
      doAutoFocusRef.current = undefined;
      let currentValue = searchValueRef.current.split(' ');
      if (currentValue.length && removeFilter) {
        currentValue = without(searchValueRef.current.split(' '), removeFilter);
      }
      if (addFilter && !currentValue.includes(addFilter)) {
        currentValue.push(addFilter);
      }
      handleSearch(currentValue.join(' '));
    },
    [handleSearch],
  );

  const returnFilterOption = (label: string, add: string, remove: string) => ({
    text: label,
    subText: '',
    icon: searchIncludes(add) ? ICONS.CheckLight : ICONS.Blank,
    onPress: () =>
      searchIncludes(add)
        ? handleAddFilter('', add)
        : handleAddFilter(add, remove),
  });

  const hasSelection = checkedIds.length > 0;
  const handleClose = React.useCallback(() => {
    if (hasSelection) {
      confirm({
        text: 'Your selection will be lost if you continue',
        confirmText: 'Discard Selection',
        cancelText: 'Cancel',
        destructive: true,
        onConfirm: () => dispatch(NAVIGATE_MODAL_BACK()),
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  }, [hasSelection, dispatch, confirm]);

  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !hasSelection });
  }, [navigation, hasSelection]);
  useEsc(handleClose);

  return (
    <Page
      header={
        <>
          <PageHeader
            title="Link Questions"
            subtitle={pluralise(
              checkedIds.length,
              '{x} Selected',
              '{x} Selected',
              'None Selected',
            )}
            onPressCancelButton={handleClose}
            onPressSaveButton={checkedIds.length ? handlePressSave : null}
          />
          <PageToolbar
            onSearch={handleSearch}
            searchValue={searchValue}
            searchPlaceholder="Search questions..."
            onSearchEsc={handleClose}
          >
            <Menu
              iconButtonConfig={{
                icon: ICONS.SlidersUpLight,
              }}
              options={[
                'Question Filters',
                returnFilterOption('Is Linked', '+link', '-link'),
                returnFilterOption('Is Not Linked', '-link', '+link'),
              ]}
            />
          </PageToolbar>
        </>
      }
    >
      {filteredQuestionItems.length ? (
        <List
          data={filteredQuestionItems}
          keyExtractor={(item) => `${item.id}`}
          renderItem={renderItem}
          extraData={{ checkedIds }}
          ItemSeparatorComponent={undefined}
        />
      ) : (
        <PageEmpty
          icon={ICONS.RBPlectrumLight}
          text={'No questions available to link'}
        />
      )}
    </Page>
  );
};
